<template>
  <MainLayout>
    <template #toolbar>
      <Shortcuts
        :main-shortcut="''"
        :shortcuts="['budget','create_advertiser','create_campaign']"
      />
    </template>
    <template>
      <!--begin::Page title-->
      <div class="d-flex text-uppercase text-center justify-content-center align-items-center mt-5 mb-10">
        <BaseIcon size="small">
          <AudienceIcon />
        </BaseIcon>
        <h1 class="text-center fw-bold ms-5 mb-0">{{ $t('explore_audiences') }}</h1>
      </div>
      <!--begin::Actions-->
      <form @submit.prevent="search" class="d-flex justify-content-center px-md-20 default-form" v-if="step === 'audiences'">
        <div class="bg-white shadow-sm mb-8 p-5 actions-audiences w-100">
          <div class="d-flex align-items-center justify-content-center gap-3">
            <div class="col-6">
              <input type="text" v-model="filters.name" class="form-control" :placeholder="$t('search_for_an_audience')" @keyup="search">
            </div>
            <div class="col-4">
              <template v-if="categoriesLoader">
                <Loader
                  :size="'small'"
                  :align="'start'"
                />
              </template>
              <div class="custom-select custom-select-sm" v-else>
                <select
                  v-model="filters.category_id"
                  class="form-control"
                >
                  <option value="">{{ $t('all_categories') }}</option>
                  <option :value="category.id" v-for="(category, index) in categories" :key="index">
                    {{ $t('categories.' + category.slug) }}
                  </option>
                </select>
              </div>
            </div>
            <button @onclick="search" class="col-1 btn btn-color1 px-3">
              <i class="fa fa-search font-size-xRegular px-3"></i>
            </button>
          </div>
        </div>
      </form>
      <!--end::Actions-->
      <!--end::Page title-->
    </template>
    <p class="text-end font-size-xSmall">{{ $t('classification_and_audience_descriptions_were_generated_by_AI') }}</p>
    <b-tabs content-class="mt-3" nav-class="default-tabs justify-content-end border-0">
      <b-tab :title="$t('all_audiences')" @click="setStep('audiences')">
        <div>
          <!--begin: Audiences Table -->
          <div class="table-responsive">
            <div v-if="localAudiences.length == 0 && loader">
              <Loader
                :size="'big'"
              />
            </div>
            <div class="text-center mt-3" v-else-if="localAudiences.length == 0 && !loader">
              <small class="fs-6 fw-semibold text-gray-700">{{ $t('no_audiences_found') }}</small>
            </div>
            <table v-else-if="localAudiences.length > 0" class="table align-middle table-row-dashed fs-6 gy-5">
              <thead v-if="!loader">
                <tr class="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                  <th></th>
                  <th class="">
                    {{ $t('name') }}
                  </th>
                  <th class="">
                    {{ $t('category') }}
                  </th>
                  <th class="min-w-20px">
                    {{ $t('actions') }}
                  </th>
                </tr>
              </thead>
              <tbody class="fw-semibold text-gray-600">
                <tr v-for="(data, index) in localAudiences" :key="index">
                  <td></td>
                  <td>
                    <span v-b-tooltip.hover :title="data.description">{{ data.name }}</span>
                  </td>
                  <td>
                    <span v-if="data.category">{{ data.category.name }}</span>
                    <span v-else>--</span>
                  </td>
                  <td>
                    <span
                      v-b-tooltip.hover
                      :title="localAudiencesFavorites.some(fav => fav.id === data.id) ? $t('already_favorited') : $t('add_to_favorites')"
                    >
                      <Button
                        :disabled="localAudiencesFavorites.some(fav => fav.id === data.id)"
                        class="btn"
                        :loading="isFavoriting[data.id]"
                        @click="!localAudiencesFavorites.some(fav => fav.id === data.id) && addFavoriteAudience(data.id)"
                      >
                        <i :class="{
                            'fa-regular fa-bookmark p-0 m-0 font-size-regular': !localAudiencesFavorites.some(fav => fav.id === data.id),
                            'fa-solid fa-bookmark p-0 m-0 font-size-regular': localAudiencesFavorites.some(fav => fav.id === data.id)
                          }"
                        ></i>
                      </Button>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="text-center mb-6" v-if="localAudiences.length > 0 && !loader && totalPages > 1">
            <div class="d-flex flex-stack flex-wrap pt-10">
              <div class="fs-6 fw-semibold text-gray-700">
                {{ $t('pagination', { from: this.from, to: this.to, totalItems: this.total }) }}
              </div>
              <b-pagination
                class="custom-pagination"
                v-model="page"
                :total-rows="total"
                :per-page="itemsPerPage"
                @input="getAudiences"
                hide-goto-end-buttons
              />
            </div>
          </div>
          <!--end: Audiences Table -->
        </div>
      </b-tab>
      <b-tab :title="$t('my_favorite_audiences')" @click="setStep('favorites')">
        <div>
          <!--begin: Audiences Table -->
          <div class="table-responsive">
            <div v-if="localAudiencesFavorites.length == 0 && loaderFavoriteAudiences">
              <Loader
                :size="'big'"
              />
            </div>
            <div class="text-center mt-3" v-if="localAudiencesFavorites.length == 0 && !loaderFavoriteAudiences">
              <small class="fs-6 fw-semibold text-gray-700">{{ $t('no_favorite_audience') }}</small>
            </div>
            <table v-else-if="localAudiencesFavorites.length > 0" class="table align-middle table-row-dashed fs-6 gy-5">
              <thead>
                <tr class="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                  <th></th>
                  <th class="min-w-100px">
                    {{ $t('name') }}
                  </th>
                  <th class="">
                    {{ $t('category') }}
                  </th>
                  <th class="min-w-20px">
                    {{ $t('actions') }}
                  </th>
                </tr>
              </thead>
              <tbody class="fw-semibold text-gray-600">
                <tr v-for="(data, index) in localAudiencesFavorites" :key="index">
                  <td></td>
                  <td>
                    <span v-b-tooltip.hover :title="data.description">{{ data.name }}</span>
                  </td>
                  <td>
                    <span v-if="data.category">{{ data.category.name }}</span>
                    <span v-else>--</span>
                  </td>
                  <td>
                    <Button class="btn" v-b-tooltip.hover :title="$t('delete')" :loading="loaderDestroyFavoriteAudiences[data.id]" @click="destroyFavoriteAudiences(data.id)"><i class="fa fa-trash p-0 m-0 font-size-regular"></i></Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="text-center mb-6" v-if="localAudiencesFavorites.length > 0 && !loaderFavoriteAudiences && totalPagesFavorites > 1">
            <div class="d-flex flex-stack flex-wrap pt-10">
              <div class="fs-6 fw-semibold text-gray-700">
                {{ $t('pagination', { from: this.fromFavorites, to: this.toFavorites, totalItems: this.totalFavorites }) }}
              </div>
              <b-pagination
                class="custom-pagination"
                v-model="pageFavorites"
                :total-rows="totalFavorites"
                :per-page="itemsPerPageFavorites"
                @input="getFavoriteAudiences"
                hide-goto-end-buttons
              />
            </div>
          </div>
          <!--end: Audiences Table -->
        </div>
      </b-tab>
    </b-tabs>
  </MainLayout>
</template>
<script>

import AudiencesService from '@/modules/audiences/services/audiences-service'
import CategoriesService from '@/modules/categories/services/categories-service'
import NotificationService from '@/modules/notifications/services/notifications-service'

import AudienceIcon from '@/components/common/Icon/AudienceIcon'
import BaseIcon from '@/components/common/Icon/BaseIcon'
import Button from '@/components/common/Button/Button'
import Loader from '@/components/common/Loader/Loader'
import MainLayout from '@/components/layouts/MainLayout'
import Shortcuts from '@/components/common/Shortcuts/Shortcuts'

import { mapState } from 'vuex'

export default {
  components: {
    AudienceIcon,
    BaseIcon,
    Button,
    MainLayout,
    Shortcuts,
    Loader
  },
  data () {
    return {
      categories: [],
      filters: {
        name: '',
        category_id: ''
      },
      from: 0,
      itemsPerPage: 0,
      isFavoriting: [],
      localAudiences: [],
      localAudiencesFavorites: [],
      loaderDestroyFavoriteAudiences: [],
      loaderFavoriteAudiences: false,
      loader: false,
      page: 1,
      step: 'audiences',
      totalPages: 0,
      total: 0,
      throttleSearch: null,
      to: 0,

      // pagination to favorites
      pageFavorites: 1,
      totalFavorites: 0,
      itemsPerPageFavorites: 0,
      totalPagesFavorites: 0,
      toFavorites: 0,
      fromFavorites: 0
    }
  },
  created () {
    this.getAudiences()
    this.getFavoriteAudiences()
    this.getCategories()
  },
  computed: {
    ...mapState('users', {
      user: state => state.user
    })
  },
  methods: {
    /**
     * Submit favorite audience
     */
    async addFavoriteAudience (audienceId) {
      this.$set(this.isFavoriting, audienceId, true)
      try {
        await AudiencesService.setFavoriteAudiences({
          audience_id: audienceId,
          company_id: this.user.company.id
        })
        const notification = {
          notification: {
            type: 'success',
            content: this.$t('added_to_favorites')
          }
        }
        NotificationService.createGlobalSuccessNotification(notification)
        this.reset()
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.$set(this.isFavoriting, audienceId, false)
    },
    /**
     * destroy favorite audiences
     */
    async destroyFavoriteAudiences (uuid) {
      this.$set(this.loaderDestroyFavoriteAudiences, uuid, true)
      try {
        await AudiencesService.destroyFavoriteAudiences(uuid)
        this.reset()
        const notification = {
          notification: {
            type: 'success',
            content: this.$t('audience_removed_from_favorites')
          }
        }
        NotificationService.createGlobalSuccessNotification(notification)
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.$set(this.loaderDestroyFavoriteAudiences, uuid, false)
    },
    /**
     * Get targetings
     */
    async getAudiences () {
      this.loader = true
      try {
        this.localAudiences = []
        const audiences = await AudiencesService.getAudiences({
          category_id: this.filters.category_id,
          name: this.filters.name,
          page: this.page,
          type: this.type
        })
        this.localAudiences = audiences.data
        this.total = audiences.meta.total
        this.itemsPerPage = audiences.meta.per_page
        this.totalPages = audiences.meta.last_page
        this.to = audiences.meta.to
        this.from = audiences.meta.from
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.loader = false
    },
    /**
     * Get audiences for search
     */
    getAudiencesForSearch () {
      this.getAudiences()
    },
    /**
     * Get categories
     */
    async getCategories () {
      this.categoriesLoader = true
      try {
        const categories = await CategoriesService.getCategories({
          type: 'AUDIENCE'
        })
        this.categories = categories.data
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.categoriesLoader = false
    },
    /**
     * get favorite audience
     */
    async getFavoriteAudiences () {
      this.loaderFavoriteAudiences = true
      try {
        const audiences = await AudiencesService.getFavoriteAudiences()
        this.localAudiencesFavorites = audiences.data
        this.totalFavorites = audiences.meta.total
        this.itemsPerPageFavorites = audiences.meta.per_page
        this.totalPagesFavorites = audiences.meta.last_page
        this.toFavorites = audiences.meta.to
        this.fromFavorites = audiences.meta.from
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.loaderFavoriteAudiences = false
    },
    /**
     * Reset Data
     */
    reset () {
      this.page = 1
      this.filters.search = ''
      this.localAudiences = []
      this.getAudiences()
      this.localAudiencesFavorites = []
      this.getFavoriteAudiences()
    },
    /**
     * Search audiences
     */
    search () {
      clearTimeout(this.throttleSearch)
      this.page = 1
      this.localAudiences = []
      this.throttleSearch = setTimeout(this.getAudiencesForSearch, 500)
    },
    /**
     * set step audiences or favorite audiences
     * @param step
     */
    setStep (step) {
      this.reset()
      switch (step) {
        case 'favorites':
          this.step = 'favorites'
          break
        case 'audiences':
          this.step = 'audiences'
          break
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .actions-audiences button {
    height: 45px;
  }
</style>
